import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import predictionIssues from '../data/predictionIssues.json';
import { loadPredictionIssue, resolveIssue, routeRequest } from '../utils/predictionIssues';
import { presentPredictionTeam } from '../utils/predictionPresentation';
import { buildSeasonScorecard } from '../utils/predictionScorecard';
import { buildProbabilityScorecard } from '../utils/probabilityScorecard';
import { band, isProspective, issuedPickProbability, wholePercent } from '../utils/winProbability';
import ForecastHero from '../components/picks/ForecastHero';
import MatchupRail from '../components/picks/MatchupRail';
import MatchupDetail from '../components/picks/MatchupDetail';
import ForecastMethods from '../components/picks/ForecastMethods';
import SeasonScorecard from '../components/picks/SeasonScorecard';
import '../components/picks/forecast.css';

// Loads every filed forecast of the season so the win-probability panel can grade all weeks.
function useSeasonForecasts(season, retry) {
  const [state, setState] = useState({ season: null, status: 'loading', byWeek: {} });
  useEffect(() => {
    if (season == null) return undefined;
    const seasonIssues = predictionIssues.issues.filter((item) => item.season === season);
    const controller = new AbortController();
    setState({ season, status: 'loading', byWeek: {} });
    Promise.all(seasonIssues.map((item) => loadPredictionIssue(item, controller.signal).then((forecast) => [item.week, forecast])))
      .then((entries) => { if (!controller.signal.aborted) setState({ season, status: 'ready', byWeek: Object.fromEntries(entries) }); })
      .catch((error) => { if (!controller.signal.aborted && error.name !== 'AbortError') setState({ season, status: 'error', byWeek: {} }); });
    return () => controller.abort();
  }, [season, retry]);
  return state;
}

// Win chance per matchup for weeks the registered model covered before kickoff; null for earlier weeks.
// From Week 5 the edition carries the issued q (validated against the recomputation when filed).
function pickChances(prediction, issue) {
  if (!prediction || !issue || !isProspective(issue)) return null;
  const teams = Object.fromEntries(prediction.teams.map((team) => [String(team.id), team]));
  return Object.fromEntries(prediction.matchups.map((matchup) => {
    const q = issuedPickProbability(matchup, teams);
    return [matchup.id, band(q) ? { q, percent: wholePercent(q), band: band(q) } : null];
  }));
}

export default function AIPicks() {
  const { year, weekSlug } = useParams();
  const request = routeRequest(year, weekSlug);
  const issue = resolveIssue(predictionIssues, request);
  const [state, setState] = useState({ status: 'loading', prediction: null, error: null, path: null });
  const [retry, setRetry] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [shouldLandOnDetail, setShouldLandOnDetail] = useState(false);
  const prediction = state.prediction;
  const scorecard = useMemo(() => buildSeasonScorecard(predictionIssues.issues, issue?.season), [issue?.season]);
  const seasonForecasts = useSeasonForecasts(issue?.season, retry);
  const probability = useMemo(() => {
    if (seasonForecasts.season !== issue?.season || seasonForecasts.status !== 'ready') return { status: seasonForecasts.status === 'error' ? 'error' : 'loading', scorecard: null };
    return { status: 'ready', scorecard: buildProbabilityScorecard(predictionIssues.issues, seasonForecasts.byWeek, issue.season) };
  }, [seasonForecasts, issue?.season]);
  const chances = useMemo(() => pickChances(prediction, issue), [prediction, issue]);
  const teamsById = useMemo(() => Object.fromEntries(
    (prediction?.teams || []).map((team) => [team.id, presentPredictionTeam(team, issue)]),
  ), [prediction, issue]);

  useEffect(() => {
    const previous = document.title;
    document.title = issue ? `${issue.label} AI Picks · The Forecast · Bruins09` : 'AI Picks · The Forecast · Bruins09';
    return () => { document.title = previous; };
  }, [issue]);

  useEffect(() => {
    if (!issue) { setState({ status: 'unavailable', prediction: null, error: null, path: null }); return undefined; }
    const controller = new AbortController();
    setState({ status: 'loading', prediction: null, error: null, path: issue.path });
    loadPredictionIssue(issue, controller.signal)
      .then((prediction) => { if (!controller.signal.aborted) setState({ status: 'ready', prediction, error: null, path: issue.path }); })
      .catch((error) => { if (!controller.signal.aborted && error.name !== 'AbortError') setState({ status: 'error', prediction: null, error, path: issue.path }); });
    return () => controller.abort();
  }, [issue, retry]);

  useEffect(() => { setSelectedId(null); }, [issue]);

  useEffect(() => {
    if (!shouldLandOnDetail || state.status !== 'ready') return;
    const detail = document.getElementById('forecast-detail');
    if (!detail) return;
    detail.focus({ preventScroll: true });
    detail.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    setShouldLandOnDetail(false);
  }, [selectedId, shouldLandOnDetail, state.status]);

  if (!issue || state.status === 'unavailable') return <IssueState title="This issue is unavailable" detail="That week has not been published in The Forecast." action={<Link to={`/ai-picks/${predictionIssues.latest.season}/week-${predictionIssues.latest.week}`}>Read the latest issue</Link>} />;
  if (state.status === 'loading' || state.path !== issue.path) return <IssueState title="Opening the issue" detail={issue.editionType ? 'Loading the published late edition.' : 'Loading the published pregame forecast.'} />;
  if (state.status === 'error') return <IssueState title="The issue could not be loaded" detail="The published forecast is unavailable right now." action={<button type="button" onClick={() => setRetry((value) => value + 1)}>Try again</button>} />;

  const matchup = prediction.matchups.find((item) => item.id === selectedId) || prediction.matchups[0];

  if (!matchup) return null;

  return <div className="forecast">
    <ForecastHero prediction={prediction} issues={predictionIssues.issues} selectedIssue={issue} record={predictionIssues.record} scorecard={scorecard} />
    <MatchupRail matchups={prediction.matchups} teamsById={teamsById} selectedId={matchup.id} onSelect={(id) => { setSelectedId(id); setShouldLandOnDetail(true); }} late={Boolean(prediction.editionType)} finalScores={issue.results?.scores} chances={chances} />
    <MatchupDetail key={`${issue.season}-${issue.week}-${matchup.id}`} matchup={matchup} teamsById={teamsById} results={issue.results} chance={chances?.[matchup.id] || null} />
    <SeasonScorecard scorecard={scorecard} selectedIssue={issue} probability={probability} />
    <ForecastMethods methodology={prediction.methodology} />
  </div>;
}

function IssueState({ title, detail, action }) {
  return <div className="forecast forecast-state"><div className="forecast-shell"><p className="forecast-kicker">The Forecast</p><h1>{title}</h1><p>{detail}</p>{action && <div>{action}</div>}</div></div>;
}
